import React, { useEffect, useState } from 'react';
import { useParams, useSearchParams, useNavigate } from 'react-router-dom';
import {
  Download, Info, Zap, Building2, Euro, Percent,
  CheckCircle2, CheckCircle, XCircle, TrendingDown, Calendar, FileText,
  ClipboardList, ShieldAlert, Lock, Unlock, Trash2, ArrowLeft, Share2, MessageSquare,
  AlertCircle, BarChart3,
} from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { useAuth } from '../contexts/AuthContext';
import ShareModal from '../components/ShareModal';
import ValideClientBadge from '../components/ValideClientBadge';
import ShareAccessBadge from '../components/ShareAccessBadge';
import RapportAuditHistory from '../components/RapportAuditHistory';
import PdfPreviewModal from '../components/PdfPreviewModal';
import EligibilityExplainer from '../components/EligibilityExplainer';
import { downloadPdf } from '../utils/downloadPdf';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const RapportTICFE = ({ readOnly = false, shareData = null, shareToken = null }) => {
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const auth = useAuth?.() || {};
  const userRole = (auth?.user?.role || '').toLowerCase();
  const isAdmin = userRole === 'admin' || userRole === 'energy_manager';
  const [rapport, setRapport] = useState(shareData);
  const [loading, setLoading] = useState(!shareData);
  const [activeTab, setActiveTab] = useState('overview');
  const [shareOpen, setShareOpen] = useState(false);
  const [pdfPreviewOpen, setPdfPreviewOpen] = useState(false);

  const pdfUrl = shareToken
    ? `${API_URL}/api/public/share/${shareToken}/pdf`
    : (id ? `${API_URL}/api/rapports/${id}/pdf` : null);
  const pdfFilename = `rapport_ticfe_${rapport?.siren || rapport?.nom_entreprise || 'client'}_${new Date().toISOString().split('T')[0]}.pdf`;

  useEffect(() => {
    if (shareData) {
      setRapport(shareData);
      setLoading(false);
      return;
    }
    const dataParam = searchParams.get('data');
    const previewId = searchParams.get('preview');
    
    if (previewId) {
      loadFromPreview(previewId);
    } else if (dataParam) {
      try {
        const decodedData = JSON.parse(decodeURIComponent(dataParam));
        setRapport(decodedData);
        setLoading(false);
      } catch (e) {
        console.error('Erreur décodage données:', e);
        loadRapport();
      }
    } else if (id) {
      loadRapport();
    } else {
      setLoading(false);
    }
  }, [id, searchParams, shareData]);

  const loadFromPreview = async (previewId) => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/ticfe/rapports/preview/${previewId}`);
      if (response.ok) {
        const data = await response.json();
        setRapport(data);
      }
    } catch (error) {
      console.error('Erreur chargement preview:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadRapport = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/rapports/${id}`);
      if (response.ok) {
        const data = await response.json();
        setRapport(data);
      }
    } catch (error) {
      console.error('Erreur chargement rapport:', error);
    } finally {
      setLoading(false);
    }
  };

  const downloadPDF = async () => {
    const url = shareToken
      ? `/api/public/share/${shareToken}/pdf`
      : `/api/rapports/${id}/pdf`;
    const filename = `rapport_ticfe_${rapport?.siren || rapport?.nom_entreprise || 'client'}_${new Date().toISOString().split('T')[0]}.pdf`;
    try {
      await downloadPdf({ url, filename, includeAuth: !shareToken });
    } catch { /* toast géré par l'utilitaire */ }
  };

  const handleDelete = async () => {
    if (!window.confirm('Supprimer ce rapport TICFE ?')) return;
    const token = localStorage.getItem('token');
    await fetch(`${API_URL}/api/rapports/${id}`, { method: 'DELETE', headers: { 'Authorization': `Bearer ${token}` } });
    navigate('/dashboard?tab=ticfe');
  };

  const handleLock = async () => {
    if (!rapport) return;
    const token = localStorage.getItem('token');
    const action = rapport.locked ? 'unlock' : 'lock';
    await fetch(`${API_URL}/api/rapports/${id}/${action}`, { method: 'POST', headers: { 'Authorization': `Bearer ${token}` } });
    loadRapport();
  };

  const formatCurrency = (value) => {
    if (value === undefined || value === null || isNaN(value)) return '0,00 €';
    return value.toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' €';
  };

  const formatNumber = (value, decimals = 2) => {
    if (value === undefined || value === null || isNaN(value)) return '0';
    return value.toLocaleString('fr-FR', { minimumFractionDigits: decimals, maximumFractionDigits: decimals });
  };

  const formatPercent = (value) => {
    if (value === undefined || value === null || isNaN(value)) return '0%';
    return (value * 100).toLocaleString('fr-FR', { minimumFractionDigits: 3, maximumFractionDigits: 3 }) + '%';
  };

  // Fonction pour calculer les périodes manquantes (future ET précédente)
  const calculerRecommandationPeriode = () => {
    if (!rapport.fin_periode || !rapport.debut_periode) return null;
    
    const debutPeriodeAnalysee = new Date(rapport.debut_periode);
    const finPeriodeAnalysee = new Date(rapport.fin_periode);
    const aujourdHui = new Date();
    
    // Calculer la durée de la période analysée en mois
    const moisAnalyses = Math.round((finPeriodeAnalysee - debutPeriodeAnalysee) / (30 * 24 * 60 * 60 * 1000));
    
    // Récupérer les économies de la période analysée
    const economiesAttestation = rapport.economies_attestation_total || 0;
    const economiesRemboursement = Object.values(rapport.economies_remboursement || {}).reduce((a, b) => a + b, 0);
    const economiesAnalysees = economiesAttestation + economiesRemboursement;
    
    // IMPORTANT: Pour les périodes manquantes, on se base UNIQUEMENT sur le remboursement (CERFA)
    // car ce sont des périodes passées où les taxes ont déjà été payées
    // L'attestation ne s'applique qu'aux 12 prochains mois de consommation future
    const economiesMensuellesRemboursement = moisAnalyses > 0 ? economiesRemboursement / moisAnalyses : 0;
    
    // Économies mensuelles totales (pour affichage)
    const economiesMensuelles = moisAnalyses > 0 ? economiesAnalysees / moisAnalyses : 0;
    
    // ===== PÉRIODE FUTURE MANQUANTE =====
    // Calculer le début des 12 prochains mois (mois suivant)
    const debutProchains12Mois = new Date(aujourdHui.getFullYear(), aujourdHui.getMonth() + 1, 1);
    
    // Calculer la fin des 12 prochains mois
    const finProchains12Mois = new Date(debutProchains12Mois);
    finProchains12Mois.setFullYear(finProchains12Mois.getFullYear() + 1);
    finProchains12Mois.setDate(finProchains12Mois.getDate() - 1);
    
    // La période future manquante commence le lendemain de la fin de période analysée
    const debutPeriodeManquante = new Date(finPeriodeAnalysee);
    debutPeriodeManquante.setDate(debutPeriodeManquante.getDate() + 1);
    
    // La période future manquante se termine la veille du début des 12 prochains mois
    const finPeriodeManquante = new Date(debutProchains12Mois);
    finPeriodeManquante.setDate(finPeriodeManquante.getDate() - 1);
    
    // Calculer le nombre de mois manquants (future)
    const moisManquants = Math.max(0, Math.round((finPeriodeManquante - debutPeriodeManquante) / (30 * 24 * 60 * 60 * 1000)));
    
    // ===== PÉRIODE FISCALE PRÉCÉDENTE =====
    // La période précédente = 12 mois avant le début de la période analysée
    // Exemple: si analyse = 09/2024-08/2025, alors période précédente = 09/2023-08/2024
    const finPeriodePrecedente = new Date(debutPeriodeAnalysee);
    finPeriodePrecedente.setDate(finPeriodePrecedente.getDate() - 1); // Veille du début de l'analyse
    
    const debutPeriodePrecedente = new Date(finPeriodePrecedente);
    debutPeriodePrecedente.setFullYear(debutPeriodePrecedente.getFullYear() - 1);
    debutPeriodePrecedente.setDate(debutPeriodePrecedente.getDate() + 1);
    
    // Vérifier si la période précédente est récupérable (pas trop ancienne)
    // On considère récupérable si elle commence après février 2022 (4 ans en arrière max pour CERFA)
    const dateMinRecuperable = new Date(aujourdHui.getFullYear() - 4, 1, 1); // Février il y a 4 ans
    const periodePrecedenteRecuperable = debutPeriodePrecedente >= dateMinRecuperable;
    
    // Économies estimées basées UNIQUEMENT sur le remboursement
    const economiesEstimees = economiesMensuellesRemboursement * moisManquants;
    const economiesEstimeesPrecedente = economiesMensuellesRemboursement * 12;
    
    const result = {
      moisAnalyses,
      economiesAnalysees,
      economiesMensuelles,
      economiesRemboursement,
      economiesMensuellesRemboursement,
      // Période future
      hasPeriodeFuture: moisManquants >= 1,
      debutProchains12Mois,
      finProchains12Mois,
      debutPeriodeManquante,
      finPeriodeManquante,
      moisManquants,
      economiesEstimees,
      // Période précédente
      hasPeriodePrecedente: periodePrecedenteRecuperable,
      debutPeriodePrecedente,
      finPeriodePrecedente,
      economiesEstimeesPrecedente
    };
    
    // Retourner seulement s'il y a au moins une recommandation
    if (result.hasPeriodeFuture || result.hasPeriodePrecedente) {
      return result;
    }
    
    return null;
  };

  const formatDateFR = (date) => {
    const mois = ['janvier', 'février', 'mars', 'avril', 'mai', 'juin', 
                  'juillet', 'août', 'septembre', 'octobre', 'novembre', 'décembre'];
    return `${mois[date.getMonth()]} ${date.getFullYear()}`;
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#0F9D7E]"></div>
      </div>
    );
  }

  if (!rapport) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <div className="text-center">
          <FileText className="w-16 h-16 text-gray-300 mx-auto mb-4" />
          <h1 className="text-2xl font-semibold text-gray-800 mb-2">Rapport non trouvé</h1>
          <p className="text-gray-500">Ce rapport n'existe pas ou les données ne sont pas disponibles</p>
        </div>
      </div>
    );
  }

  // Extraction des données
  const totalConsoMWh = rapport.total_conso_mwh || rapport.pdls_data?.reduce((sum, p) => sum + (p.total_conso_mwh || 0), 0) || 0;
  const totalTICFECerfaOfficiel = rapport.total_ticfe_cerfa_officiel || rapport.pdls_data?.reduce((sum, p) => sum + (p.ticfe_cerfa_officiel || 0), 0) || 0;
  // Ratio d'éligibilité GLOBAL (entreprise) = TICFE CERFA total / VAC. Recalculé ici pour
  // rester cohérent avec le TICFE et la VAC affichés (corrige aussi les anciens rapports
  // où ratio_global avait été stocké comme moyenne des ratios par PDL — divisé par le nb de PDL).
  const ratioGlobal = (rapport.valeur_ajoutee && rapport.valeur_ajoutee > 0)
    ? (totalTICFECerfaOfficiel / rapport.valeur_ajoutee)
    : (rapport['ratio_global'] || 0);
  const totalTICFEReel = rapport.total_ticfe_reel || rapport.pdls_data?.reduce((sum, p) => sum + (p.total_ticfe || 0), 0) || 0;
  const totalEconomiesAttestation = rapport.economies_attestation_total || 0;
  // Plafond CERFA : on ne peut plus récupérer au-delà de N-2 (deadline 31/12 de la 2e année qui suit)
  const MIN_ANNEE_RECUPERABLE = new Date().getFullYear() - 2;
  const economiesRemboursementFiltre = Object.entries(rapport.economies_remboursement || {})
    .filter(([annee]) => parseInt(annee, 10) >= MIN_ANNEE_RECUPERABLE);
  const totalEconomiesRemboursement = economiesRemboursementFiltre.reduce((s, [, v]) => s + v, 0);
  const isEligible = rapport.eligibilite_globale === 'ELIGIBLE';

  // Données pour graphiques
  // Historique : années récupérables réellement présentes dans le rapport
  const economiesParAnnee = economiesRemboursementFiltre.map(([annee, montant]) => ({
    annee, montant, estimation: false,
  }));

  // Estimations N-1 et N+1 pour visualiser la tendance (en attendant validation éligibilité)
  const economiesParAnneeAvecEstimations = (() => {
    if (economiesParAnnee.length === 0) return [];
    const sortedReal = [...economiesParAnnee].sort((a, b) => a.annee.localeCompare(b.annee));
    const avgReal = sortedReal.reduce((s, e) => s + e.montant, 0) / sortedReal.length;
    const minYear = parseInt(sortedReal[0].annee, 10);
    const maxYear = parseInt(sortedReal[sortedReal.length - 1].annee, 10);
    const series = [...sortedReal];
    // N-1 estimée (uniquement si récupérable selon le plafond CERFA)
    if (minYear - 1 >= MIN_ANNEE_RECUPERABLE) {
      series.unshift({ annee: String(minYear - 1), montant: avgReal, estimation: true });
    }
    // N+1 estimée (pas de plafond futur)
    series.push({ annee: String(maxYear + 1), montant: avgReal, estimation: true });
    return series;
  })();

  // === Libellés des périodes (cohérence affichage TICFE) ===
  // Période de couverture de l'attestation TICFE (12 mois glissants à compter du dépôt)
  const dateAujourdhuiTicfe = new Date();
  const dateAttFinTicfe = new Date(dateAujourdhuiTicfe);
  dateAttFinTicfe.setFullYear(dateAttFinTicfe.getFullYear() + 1);
  const anneeAttDebutTicfe = dateAujourdhuiTicfe.getFullYear();
  const anneeAttFinTicfe = dateAttFinTicfe.getFullYear();
  const libellePeriodeAttestation = anneeAttDebutTicfe === anneeAttFinTicfe
    ? `${anneeAttDebutTicfe}`
    : `${anneeAttDebutTicfe} → ${anneeAttFinTicfe}`;
  const libellePeriodeRemboursement = economiesParAnnee.map(e => e.annee).sort().join(', ') || '—';

  // Décomposition explicite : total_potentiel = attestation + remb_reel + remb_estime
  const totalEconomiesRemboursementEstime = economiesParAnneeAvecEstimations
    .filter(e => e.estimation)
    .reduce((s, e) => s + e.montant, 0);
  const totalPotentiel = totalEconomiesAttestation + totalEconomiesRemboursement + totalEconomiesRemboursementEstime;

  // Données du graphique global : remboursement (réel + estimé) + attestation à la fin
  const graphiqueGlobal = [
    ...economiesParAnneeAvecEstimations.map(e => ({
      label: e.annee,
      montant: e.montant,
      type: e.estimation ? 'estime' : 'reel',
      typeLabel: e.estimation ? 'Remboursement estimé' : 'Remboursement réel',
    })),
    ...(totalEconomiesAttestation > 0 ? [{
      label: libellePeriodeAttestation,
      montant: totalEconomiesAttestation,
      type: 'attestation',
      typeLabel: 'Attestation (économie future)',
    }] : []),
  ];

  // Liste des années réelles pour la Section 1 (mise en avant)
  const realYearsTicfe = [...economiesParAnnee].sort((a, b) => a.annee.localeCompare(b.annee));

  const consoParPDL = rapport.pdls_data?.map(pdl => ({
    name: pdl.pdl?.slice(-6) || 'PDL',
    conso: pdl.total_conso_mwh || 0,
    ticfe: pdl.total_ticfe || 0
  })) || [];

  const tabs = [
    { id: 'overview', label: "Vue d'ensemble" },
    { id: 'analysis', label: 'Analyse financière' },
    { id: 'details', label: 'Détails PDL' },
    { id: 'eligibility', label: 'Éligibilité' },
    { id: 'informations', label: 'Informations' }
  ];

  return (
    <div className="min-h-screen bg-[#F8FAFC]" data-testid="rapport-ticfe">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 sticky top-0 z-50">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            {!readOnly && (
              <button onClick={() => navigate('/dashboard?tab=ticfe')} className="p-2 hover:bg-[#F5F6F9] rounded-lg" data-testid="rapport-back-btn">
                <ArrowLeft className="w-5 h-5 text-[#687175]" />
              </button>
            )}
            <img src="/logo.jpg" alt="Place des Énergies" className="h-10" onError={(e) => { e.target.style.display = 'none'; }} />
            <span className="text-gray-400 text-sm">Rapport d'Éligibilité TICFE{readOnly ? ' — Lecture seule' : ''}</span>
          </div>
          <div className="flex gap-2">
            {readOnly ? (
              <>
                <button onClick={() => window.print()} className="p-2 hover:bg-[#F5F6F9] rounded-lg" title="Imprimer" data-testid="print-btn">
                  <FileText className="w-5 h-5 text-[#687175]" />
                </button>
                <button
                  onClick={() => setPdfPreviewOpen(true)}
                  className="flex items-center gap-2 bg-[#397DFF] text-white px-4 py-2 rounded-lg text-sm hover:bg-[#4672B6]"
                  data-testid="download-pdf-btn"
                >
                  <Download className="w-4 h-4" /> PDF
                </button>
              </>
            ) : id && (
              <>
                <ShareAccessBadge rapportType="ticfe" rapportId={id} />
                <button onClick={() => setShareOpen(true)} className="p-2 hover:bg-[#F5F6F9] rounded-lg" title="Partager (lecture seule)" data-testid="share-btn">
                  <Share2 className="w-5 h-5 text-[#687175]" />
                </button>
                <ValideClientBadge
                  valide={rapport.valide_client}
                  canToggle={isAdmin}
                  endpoint={`/rapports/${rapport.id}/toggle-valide-client`}
                  onUpdated={(v) => setRapport({ ...rapport, valide_client: v })}
                />
                {isAdmin && (
                  <button onClick={handleLock} className="p-2 hover:bg-[#F5F6F9] rounded-lg" title={rapport.locked ? 'Déverrouiller' : 'Verrouiller'} data-testid="lock-btn">
                    {rapport.locked ? <Lock className="w-5 h-5 text-[#397DFF]" /> : <Unlock className="w-5 h-5 text-[#687175]" />}
                  </button>
                )}
                <button
                  onClick={() => setPdfPreviewOpen(true)}
                  className="flex items-center gap-2 bg-[#397DFF] text-white px-4 py-2 rounded-lg text-sm hover:bg-[#4672B6]"
                  data-testid="download-pdf-btn"
                >
                  <Download className="w-4 h-4" /> PDF
                </button>
                {isAdmin && (
                  <button onClick={handleDelete} className="p-2 text-red-500 hover:bg-red-50 rounded-lg" data-testid="delete-btn"><Trash2 className="w-5 h-5" /></button>
                )}
              </>
            )}
          </div>
        </div>
      </header>

      {!readOnly && id && (
        <ShareModal
          open={shareOpen}
          onClose={() => setShareOpen(false)}
          rapportType="ticfe"
          rapportId={id}
          rapportLabel={rapport?.nom_entreprise || 'Rapport TICFE'}
        />
      )}

      <PdfPreviewModal
        open={pdfPreviewOpen}
        onClose={() => setPdfPreviewOpen(false)}
        url={pdfUrl}
        title={`Rapport TICFE — ${rapport?.nom_entreprise || rapport?.siren || ''}`}
        filename={pdfFilename}
      />

      {/* Contenu principal */}
      <main className="max-w-6xl mx-auto px-6 py-8">
        {/* Titre */}
        <div className="mb-6">
          <h1 className="text-3xl font-bold text-gray-900 tracking-tight flex items-center gap-3">
            <Zap className="w-8 h-8 text-[#397DFF]" />
            {rapport.nom_entreprise?.toUpperCase() || 'ENTREPRISE'}
          </h1>
          <div className="flex flex-wrap gap-4 mt-2 text-gray-500 text-sm">
            <span>SIREN : <span className="font-mono">{rapport.siren || '-'}</span></span>
            <span className="text-gray-300">|</span>
            <span>Période : <span className="font-medium">{rapport.debut_periode ? new Date(rapport.debut_periode).toLocaleDateString('fr-FR') : '-'} → {rapport.fin_periode ? new Date(rapport.fin_periode).toLocaleDateString('fr-FR') : '-'}</span></span>
            <span className="text-gray-300">|</span>
            <span>Ratio TICFE/VA : <span className="font-semibold text-gray-700">{formatPercent(ratioGlobal)}</span></span>
            <span className="text-gray-300">|</span>
            <span className={`font-semibold ${isEligible ? 'text-emerald-600' : 'text-red-500'}`} data-testid="rapport-eligibilite-globale">
              {rapport.eligibilite_globale}
            </span>
          </div>
        </div>

        {/* Onglets */}
        <div className="bg-[#EEF2F6] rounded-lg p-1 mb-6 flex">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              data-testid={`tab-${tab.id}`}
              className={`flex-1 py-2.5 px-4 text-sm font-medium rounded-md transition-all ${
                activeTab === tab.id
                  ? 'bg-white text-gray-900 shadow-sm'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* TAB 1: Vue d'ensemble */}
        {activeTab === 'overview' && (
          <div className="space-y-6">
            {/* Carte d'information TICFE */}
            <div className="bg-white rounded-xl border border-gray-200 p-6">
              <div className="flex items-start gap-3 mb-4">
                <Info className="w-5 h-5 text-gray-400 mt-0.5" />
                <h3 className="font-semibold text-gray-900">Qu'est-ce que la TICFE ?</h3>
              </div>
              <div className="text-gray-600 text-sm leading-relaxed space-y-3 ml-8">
                <p>
                  La <strong>TICFE</strong> (Taxe Intérieure sur la Consommation Finale d'Électricité), anciennement 
                  appelée CSPE, est due par tous les consommateurs d'électricité au prorata de leur consommation.
                </p>
                <p>
                  Certaines entreprises peuvent bénéficier de <strong>taux réduits</strong> si leur ratio 
                  consommation/valeur ajoutée dépasse 0,5%.
                </p>
                <div className="mt-4 p-3 bg-emerald-50 rounded-lg flex items-start gap-2">
                  <span className="text-emerald-500">💡</span>
                  <p className="text-emerald-800">
                    <strong>Bon à savoir :</strong> Les entreprises électro-intensives peuvent récupérer jusqu'à 
                    95% de la TICFE payée via le système d'attestation ou de remboursement.
                  </p>
                </div>
              </div>
            </div>

            {/* Résultat d'éligibilité */}
            <div className={`rounded-xl border-2 p-6 ${
              isEligible 
                ? 'border-emerald-200 bg-gradient-to-br from-emerald-50 to-white' 
                : 'border-red-200 bg-gradient-to-br from-red-50 to-white'
            }`}>
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-gray-500 text-sm mb-1">Résultat d'éligibilité TICFE</p>
                  <p className={`text-4xl font-bold ${isEligible ? 'text-emerald-600' : 'text-red-500'}`}>
                    {rapport.eligibilite_globale}
                  </p>
                  <p className="text-gray-500 text-sm mt-2">
                    Ratio : {formatPercent(ratioGlobal)} {ratioGlobal >= 0.005 ? '(≥ 0,5%)' : '(< 0,5%)'}
                  </p>
                </div>
                <div className={`p-4 rounded-full ${isEligible ? 'bg-emerald-100' : 'bg-red-100'}`}>
                  {isEligible ? (
                    <CheckCircle2 className="w-12 h-12 text-emerald-500" />
                  ) : (
                    <XCircle className="w-12 h-12 text-red-500" />
                  )}
                </div>
              </div>
            </div>

            {/* Encart pédagogique : comprendre l'éligibilité */}
            <EligibilityExplainer
              type="ticfe"
              eligible={isEligible}
              data={{
                ratio_global: ratioGlobal,
                valeur_ajoutee: rapport.valeur_ajoutee,
                total_conso_mwh: totalConsoMWh,
                ticfe_cerfa: totalTICFECerfaOfficiel,
              }}
              accentColor="#397DFF"
            />

            {/* Statistiques - Détails de vérification */}
            <div className="bg-white rounded-xl border border-gray-200 p-6">
              <h3 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
                <ClipboardList className="w-5 h-5 text-gray-400" />
                Détails du calcul d'éligibilité
              </h3>
              <div className="grid grid-cols-2 gap-6">
                {/* Colonne gauche : Données d'entrée */}
                <div className="space-y-3">
                  <div className="flex justify-between items-center py-2 border-b border-gray-100">
                    <span className="text-sm text-gray-500">Valeur Ajoutée Comptable (VAC)</span>
                    <span className="font-mono font-semibold text-gray-900" data-testid="rapport-vac">{formatCurrency(rapport.valeur_ajoutee)}</span>
                  </div>
                  <div className="flex justify-between items-center py-2 border-b border-gray-100">
                    <span className="text-sm text-gray-500">Consommation totale</span>
                    <span className="font-mono font-semibold text-gray-900" data-testid="rapport-conso">{formatNumber(totalConsoMWh, 2)} MWh</span>
                  </div>
                  <div className="flex justify-between items-center py-2 border-b border-gray-100">
                    <span className="text-sm text-gray-500">TICFE CERFA officiel<sup className="text-blue-600 font-bold ml-0.5">*</sup> (tarif 22,5 €/MWh)</span>
                    <span className="font-mono font-semibold text-blue-700" data-testid="rapport-ticfe-cerfa">{formatCurrency(totalTICFECerfaOfficiel)}</span>
                  </div>
                  <div className="flex justify-between items-center py-2 border-b border-gray-100">
                    <span className="text-sm text-gray-500">TICFE réel<sup className="text-blue-600 font-bold ml-0.5">**</sup> (tarifs par tension)</span>
                    <span className="font-mono font-semibold text-gray-700" data-testid="rapport-ticfe-reel">{formatCurrency(totalTICFEReel)}</span>
                  </div>
                </div>

                {/* Colonne droite : Résultats */}
                <div className="space-y-3">
                  <div className={`flex justify-between items-center py-2 border-b-2 ${ratioGlobal >= 0.005 ? 'border-emerald-200' : 'border-red-200'}`}>
                    <span className="text-sm font-medium text-gray-700">Ratio TICFE CERFA / VAC</span>
                    <span className={`font-mono font-bold text-lg ${ratioGlobal >= 0.005 ? 'text-emerald-600' : 'text-red-500'}`} data-testid="rapport-ratio">
                      {formatPercent(ratioGlobal)}
                    </span>
                  </div>
                  <div className="flex justify-between items-center py-2 border-b border-gray-100">
                    <span className="text-sm text-gray-500">Seuil d'éligibilité</span>
                    <span className="font-mono text-gray-500">0,500 %</span>
                  </div>
                  {isEligible && (
                    <>
                      <div className="flex justify-between items-center py-2 border-b border-gray-100">
                        <span className="text-sm text-gray-500">Économies attestation (12 mois)</span>
                        <span className="font-mono font-semibold text-emerald-600" data-testid="rapport-eco-attestation">{formatCurrency(totalEconomiesAttestation)}</span>
                      </div>
                      <div className="flex justify-between items-center py-2 border-b border-gray-100">
                        <span className="text-sm text-gray-500">Économies remboursement</span>
                        <span className="font-mono font-semibold text-emerald-600" data-testid="rapport-eco-remboursement">{formatCurrency(totalEconomiesRemboursement)}</span>
                      </div>
                      <div className="flex justify-between items-center py-2 bg-emerald-50 rounded-lg px-3">
                        <span className="text-sm font-medium text-emerald-800">Total économies potentielles</span>
                        <span className="font-mono font-bold text-emerald-700 text-lg" data-testid="rapport-eco-total">{formatCurrency(totalEconomiesAttestation + totalEconomiesRemboursement)}</span>
                      </div>
                    </>
                  )}
                </div>
              </div>

              {/* Légende explicative CERFA vs Réel */}
              <div className="mt-4 pt-4 border-t border-gray-100 text-xs text-gray-500 leading-relaxed space-y-1.5" data-testid="cerfa-reel-legend">
                <p>
                  <sup className="text-blue-600 font-bold">*</sup> <span className="font-medium text-gray-700">TICFE CERFA officiel</span> : taxe calculée au tarif administratif unique (22,5 €/MWh) retenu par l'administration fiscale pour le ratio d'éligibilité et le remboursement via le formulaire CERFA. C'est la valeur de référence pour la démarche de récupération.
                </p>
                <p>
                  <sup className="text-blue-600 font-bold">**</sup> <span className="font-medium text-gray-700">TICFE réel</span> : taxe effectivement payée à votre fournisseur, calculée sur les tarifs réglementés par domaine de tension (HTA / BT&nbsp;inf / BT&nbsp;sup) variables d'un mois à l'autre. La différence entre les deux représente l'avantage tarifaire réel à activer.
                </p>
              </div>
            </div>
            {(() => {
              const commentaireGlobal = (rapport.commentaire_global || '').trim();
              const commentairesSites = (rapport.sites || [])
                .map((s) => ({ nom: s.nom_site || s.siret, comment: (s.commentaire || '').trim() }))
                .filter((s) => s.comment);
              if (!commentaireGlobal && commentairesSites.length === 0) return null;
              return (
                <div className="bg-white rounded-xl border border-gray-200 p-8" data-testid="section-commentaires">
                  <h2 className="text-lg font-semibold text-[#191D1E] mb-4 flex items-center gap-2">
                    <MessageSquare className="w-5 h-5 text-[#397DFF]" /> Commentaires de l'analyste
                  </h2>
                  {commentaireGlobal && (
                    <div className="mb-4">
                      <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">Commentaire général</p>
                      <p className="text-sm text-gray-700 leading-relaxed whitespace-pre-wrap">{commentaireGlobal}</p>
                    </div>
                  )}
                  {commentairesSites.length > 0 && (
                    <div className="space-y-3 mt-4 pt-4 border-t border-gray-100">
                      <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Commentaires par site</p>
                      {commentairesSites.map((s, idx) => (
                        <div key={idx} className="bg-[#F5F8FF] border border-[#E0E7FF] rounded-lg p-3">
                          <p className="text-xs font-semibold text-[#397DFF] mb-1">{s.nom}</p>
                          <p className="text-sm text-gray-700 whitespace-pre-wrap">{s.comment}</p>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            })()}
          </div>
        )}

        {/* TAB 2: Analyse financière */}
        {activeTab === 'analysis' && (
          <div className="space-y-6">
            {isEligible ? (
              <>
                {/* ============================================ */}
                {/* SECTION 1 — ÉCONOMIES VALIDÉES (mise en avant) */}
                {/* ============================================ */}
                {totalEconomiesRemboursement > 0 && (
                  <div className="bg-gradient-to-br from-emerald-50 to-emerald-100/70 rounded-xl border-2 border-emerald-400 p-6 shadow-sm" data-testid="section-economies-validees">
                    <div className="flex items-start justify-between mb-4 flex-wrap gap-3">
                      <div className="flex items-start gap-3">
                        <div className="w-10 h-10 rounded-full bg-emerald-500 flex items-center justify-center text-white font-bold text-lg shrink-0">1</div>
                        <div>
                          <h3 className="font-bold text-emerald-900 text-lg flex items-center gap-2">
                            <CheckCircle className="w-5 h-5 text-emerald-600" /> Économies validées — Période analysée
                          </h3>
                          <p className="text-sm text-emerald-800/70 mt-0.5">
                            Remboursement CERFA chiffré sur les <strong>données réelles fournies</strong>{libellePeriodeRemboursement && libellePeriodeRemboursement !== '—' ? ` (${libellePeriodeRemboursement})` : ''}.
                          </p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="text-xs text-emerald-700 uppercase tracking-wide">Total</p>
                        <p className="text-3xl font-extrabold text-emerald-700 font-mono" data-testid="total-remb-reel">{formatCurrency(totalEconomiesRemboursement)}</p>
                      </div>
                    </div>
                    {realYearsTicfe.length > 0 && (
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-4">
                        {realYearsTicfe.map(({ annee, montant }) => (
                          <div key={annee} className="bg-white rounded-lg p-4 text-center border-2 border-emerald-200 shadow-sm" data-testid={`card-real-${annee}`}>
                            <p className="text-xs font-semibold text-emerald-700 uppercase tracking-wide mb-1">Année {annee}</p>
                            <p className="text-xl font-bold text-emerald-600 font-mono">{formatCurrency(montant)}</p>
                            <p className="text-[10px] text-gray-500 mt-1">CERFA chiffré</p>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}

                {/* ============================================ */}
                {/* SECTION 2 — REMBOURSEMENT À CONFIRMER (estimations) */}
                {/* ============================================ */}
                {totalEconomiesRemboursementEstime > 0 && (
                  <div className="bg-gradient-to-br from-purple-50 to-purple-100/60 rounded-xl border-2 border-purple-300 border-dashed p-6" data-testid="section-economies-estimees">
                    <div className="flex items-start justify-between mb-4 flex-wrap gap-3">
                      <div className="flex items-start gap-3">
                        <div className="w-10 h-10 rounded-full bg-purple-500 flex items-center justify-center text-white font-bold text-lg shrink-0">2</div>
                        <div>
                          <h3 className="font-bold text-purple-900 text-lg flex items-center gap-2">
                            <AlertCircle className="w-5 h-5 text-purple-600" /> Remboursement à confirmer — Périodes connexes
                          </h3>
                          <p className="text-sm text-purple-800/70 mt-0.5">
                            <strong>Estimations</strong> basées sur la moyenne des années renseignées. Éligibilité à confirmer avant démarche CERFA (tarif, NAF, VAC).
                          </p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="text-xs text-purple-700 uppercase tracking-wide">Sous-total estimé</p>
                        <p className="text-3xl font-extrabold text-purple-700 font-mono" data-testid="total-remb-estime">≈ {formatCurrency(totalEconomiesRemboursementEstime)}</p>
                      </div>
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-4">
                      {economiesParAnneeAvecEstimations.filter(e => e.estimation).map(({ annee, montant }) => (
                        <div key={annee} className="bg-white border-2 border-dashed border-purple-300 rounded-lg p-4 text-center" data-testid={`card-estime-${annee}`}>
                          <div className="flex items-center justify-center gap-1.5 mb-1">
                            <p className="text-xs font-semibold text-purple-700 uppercase tracking-wide">Année {annee}</p>
                            <span className="px-1.5 py-0.5 bg-purple-100 text-purple-700 rounded text-[10px] font-bold uppercase">Estimé</span>
                          </div>
                          <p className="text-xl font-bold text-purple-700 font-mono">≈ {formatCurrency(montant)}</p>
                          <p className="text-[10px] text-gray-500 mt-1">À confirmer</p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* ============================================ */}
                {/* SECTION 3 — ATTESTATION (économies potentielles futures) */}
                {/* ============================================ */}
                {totalEconomiesAttestation > 0 && (
                  <div className="bg-gradient-to-br from-blue-50 to-blue-100/60 rounded-xl border-2 border-blue-300 p-6" data-testid="section-economies-attestation">
                    <div className="flex items-start justify-between mb-4 flex-wrap gap-3">
                      <div className="flex items-start gap-3">
                        <div className="w-10 h-10 rounded-full bg-blue-500 flex items-center justify-center text-white font-bold text-lg shrink-0">3</div>
                        <div>
                          <h3 className="font-bold text-blue-900 text-lg flex items-center gap-2">
                            <FileText className="w-5 h-5 text-blue-600" /> Attestation — Économies potentielles futures
                          </h3>
                          <p
                            className="text-sm text-blue-800/70 mt-0.5"
                            title="L'attestation TICFE prend effet à compter de son dépôt et couvre 12 mois glissants."
                          >
                            Effet juridique à compter du dépôt, sur <strong>12 mois glissants</strong>.
                          </p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="text-xs text-blue-700 uppercase tracking-wide">Total</p>
                        <p className="text-3xl font-extrabold text-blue-700 font-mono" data-testid="total-attestation">{formatCurrency(totalEconomiesAttestation)}</p>
                      </div>
                    </div>
                    <div className="bg-white rounded-lg p-4 border-2 border-blue-200 text-center">
                      <p className="text-xs font-semibold text-blue-700 uppercase tracking-wide mb-1">
                        Période couverte
                      </p>
                      <p className="text-lg font-bold text-gray-900" data-testid="periode-attestation">{libellePeriodeAttestation}</p>
                      <p className="text-[11px] text-gray-500 mt-1">À renouveler chaque année tant que l'éligibilité est maintenue</p>
                    </div>
                    <div className="mt-3 bg-blue-100/60 border border-blue-200 rounded-lg p-3 text-sm text-blue-900 leading-relaxed" data-testid="note-eligibilite-attestation">
                      <strong>⚠️ Confirmation d'éligibilité requise</strong> — L'éligibilité au motif appliqué sur l'attestation
                      (process électro-intensif, double usage, secteur exposé…) devra être confirmée
                      <strong> au terme de l'exercice fiscal couvert</strong>, dans un délai de <strong>6 mois</strong>.
                      Toute évolution de l'activité (changement de NAF, de tarif, ou perte de la voie d'éligibilité) doit
                      nous être signalée pour ajuster ou cesser l'attestation en cours.
                    </div>
                  </div>
                )}

                {/* ============================================ */}
                {/* SECTION 4 — GRAPHIQUE GLOBAL                  */}
                {/* ============================================ */}
                {graphiqueGlobal.length > 0 && (
                  <div className="bg-white rounded-xl border border-gray-200 p-6" data-testid="section-graphique-global">
                    <div className="flex items-start gap-3 mb-4">
                      <div className="w-10 h-10 rounded-full bg-gray-700 flex items-center justify-center text-white font-bold text-lg shrink-0">4</div>
                      <div>
                        <h3 className="font-bold text-gray-900 text-lg flex items-center gap-2">
                          <BarChart3 className="w-5 h-5 text-gray-700" /> Vue d'ensemble — Toutes les économies
                        </h3>
                        <p className="text-sm text-gray-500 mt-0.5">Visualisation cumulée du remboursement CERFA (réel + estimé) et de l'attestation par période.</p>
                      </div>
                    </div>

                    {/* Légende synthétique */}
                    <div className="flex flex-wrap gap-3 mb-4 text-xs">
                      <div className="flex items-center gap-1.5"><span className="w-4 h-4 rounded bg-emerald-500" /> Remboursement réel</div>
                      <div className="flex items-center gap-1.5"><span className="w-4 h-4 rounded" style={{ background: 'repeating-linear-gradient(45deg, #DDD6FE, #DDD6FE 3px, #8B5CF6 3px, #8B5CF6 5px)' }} /> Remboursement estimé</div>
                      <div className="flex items-center gap-1.5"><span className="w-4 h-4 rounded bg-blue-500" /> Attestation (futur)</div>
                    </div>

                    <div className="h-72">
                      <ResponsiveContainer width="100%" height="100%">
                        <BarChart
                          data={graphiqueGlobal}
                          margin={{ top: 10, right: 30, left: 20, bottom: 5 }}
                          barCategoryGap="25%"
                        >
                          <defs>
                            <pattern id="hatchPattern-ticfe" patternUnits="userSpaceOnUse" width="6" height="6" patternTransform="rotate(45)">
                              <rect width="6" height="6" fill="#DDD6FE" />
                              <line x1="0" y1="0" x2="0" y2="6" stroke="#8B5CF6" strokeWidth="2" />
                            </pattern>
                          </defs>
                          <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                          <XAxis dataKey="label" />
                          <YAxis tickFormatter={(v) => `${(v / 1000).toFixed(0)}k€`} />
                          <Tooltip
                            formatter={(v, name, props) => [
                              `${props.payload?.type === 'estime' ? '≈ ' : ''}${formatCurrency(v)}`,
                              props.payload?.typeLabel || 'Économie'
                            ]}
                          />
                          <Bar
                            dataKey="montant"
                            name="Économie"
                            maxBarSize={90}
                            radius={[4, 4, 0, 0]}
                            shape={(props) => {
                              const { x, y, width, height, payload } = props;
                              let fill = '#10B981';
                              if (payload?.type === 'estime') fill = 'url(#hatchPattern-ticfe)';
                              else if (payload?.type === 'attestation') fill = '#3B82F6';
                              return <rect x={x} y={y} width={width} height={height} fill={fill} rx={4} />;
                            }}
                          />
                        </BarChart>
                      </ResponsiveContainer>
                    </div>

                    <p className="text-xs text-gray-600 mt-3 px-2 leading-relaxed bg-gray-50 border border-gray-200 rounded p-3" data-testid="chart-info-note">
                      <strong>Lecture :</strong> chaque barre représente une période. Le remboursement CERFA est rétroactif sur les années passées ;
                      l'attestation s'applique sur les 12 mois suivant son dépôt. Les barres hachurées violettes sont des estimations basées sur la moyenne
                      des années renseignées et doivent être confirmées avant toute démarche CERFA.
                    </p>
                  </div>
                )}

                {/* ============================================ */}
                {/* SECTION 5 — TOTAL POTENTIEL                   */}
                {/* ============================================ */}
                <div className="bg-gradient-to-r from-[#F97316] to-[#EA580C] rounded-xl p-6 text-white" data-testid="kpi-total-economies">
                  <div className="flex items-center justify-between mb-3">
                    <div>
                      <p className="text-orange-100 text-sm mb-1 uppercase tracking-wider">Économie totale potentielle</p>
                      <p className="text-5xl font-extrabold font-mono" data-testid="total-potentiel">+{formatCurrency(totalPotentiel)}</p>
                    </div>
                    <TrendingDown className="w-16 h-16 text-orange-200" />
                  </div>
                  <div className="bg-white/10 rounded-lg p-3 space-y-1.5 text-sm">
                    {totalEconomiesRemboursement > 0 && (
                      <div className="flex items-center justify-between">
                        <span className="text-orange-50">
                          <span className="inline-block w-2 h-2 rounded-full bg-emerald-300 mr-1.5" />
                          <strong>① Remboursement réel</strong> · {libellePeriodeRemboursement} <span className="text-orange-200 text-xs">(CERFA chiffré)</span>
                        </span>
                        <span className="font-mono font-semibold">{formatCurrency(totalEconomiesRemboursement)}</span>
                      </div>
                    )}
                    {totalEconomiesRemboursementEstime > 0 && (
                      <div className="flex items-center justify-between">
                        <span className="text-orange-50">
                          <span className="inline-block w-2 h-2 rounded-full bg-purple-300 mr-1.5" />
                          <strong>② Remboursement estimé</strong> · {economiesParAnneeAvecEstimations.filter(e => e.estimation).map(e => e.annee).join(', ')} <span className="text-orange-200 text-xs">(à confirmer)</span>
                        </span>
                        <span className="font-mono font-semibold">≈ {formatCurrency(totalEconomiesRemboursementEstime)}</span>
                      </div>
                    )}
                    {totalEconomiesAttestation > 0 && (
                      <div className="flex items-center justify-between">
                        <span className="text-orange-50">
                          <span className="inline-block w-2 h-2 rounded-full bg-blue-300 mr-1.5" />
                          <strong>③ Attestation</strong> · {libellePeriodeAttestation}
                        </span>
                        <span className="font-mono font-semibold">{formatCurrency(totalEconomiesAttestation)}</span>
                      </div>
                    )}
                    <div className="border-t border-white/30 pt-1.5 flex items-center justify-between font-bold">
                      <span>Total potentiel</span>
                      <span className="font-mono">{formatCurrency(totalPotentiel)}</span>
                    </div>
                  </div>
                </div>

                {/* Recommandation période manquante */}
                {(() => {
                  const recommandation = calculerRecommandationPeriode();
                  if (!recommandation) return null;
                  
                  return (
                    <div className="bg-blue-50 rounded-xl border-2 border-blue-200 p-6">
                      <div className="flex items-start gap-4">
                        <div className="bg-blue-100 rounded-full p-3">
                          <Calendar className="w-6 h-6 text-blue-600" />
                        </div>
                        <div className="flex-1">
                          <h3 className="font-semibold text-gray-900 mb-2">
                            📅 Recommandations : Optimisation des périodes d'analyse
                          </h3>
                          <div className="space-y-3 text-sm text-gray-700">
                            <p>
                              <span className="font-medium">Période analysée :</span>{' '}
                              {rapport.debut_periode ? new Date(rapport.debut_periode).toLocaleDateString('fr-FR') : '-'}{' '}
                              au {rapport.fin_periode ? new Date(rapport.fin_periode).toLocaleDateString('fr-FR') : '-'}
                              {recommandation.moisAnalyses > 0 && (
                                <span className="text-gray-500"> ({recommandation.moisAnalyses} mois)</span>
                              )}
                            </p>
                            
                            {/* Estimation globale des économies */}
                            <div className="bg-emerald-50 border border-emerald-300 rounded-lg p-4 mt-3">
                              <p className="font-semibold text-emerald-800 mb-2">💰 Base de calcul</p>
                              <div className="grid grid-cols-2 gap-4 text-sm">
                                <div>
                                  <p className="text-gray-600">Économies période analysée :</p>
                                  <p className="font-bold text-gray-900">{formatCurrency(recommandation.economiesAnalysees)}</p>
                                  <p className="text-xs text-gray-500">(Attestation + Remboursement)</p>
                                </div>
                                <div>
                                  <p className="text-gray-600">Remboursement mensuel moyen :</p>
                                  <p className="font-bold text-gray-900">{formatCurrency(recommandation.economiesMensuellesRemboursement)}/mois</p>
                                  <p className="text-xs text-gray-500">(Base pour les estimations)</p>
                                </div>
                              </div>
                            </div>
                            
                            {/* PÉRIODE FUTURE MANQUANTE */}
                            {recommandation.hasPeriodeFuture && recommandation.moisManquants > 0 && (
                              <div className="bg-amber-50 border border-amber-300 rounded-lg p-4 mt-4">
                                <p className="font-semibold text-amber-800 mb-2">
                                  ⏩ Période future non couverte (remboursement possible)
                                </p>
                                <p className="text-gray-700 mb-2">
                                  <span className="font-medium">Économies attestation valables pour :</span>{' '}
                                  {formatDateFR(recommandation.debutProchains12Mois)} à {formatDateFR(recommandation.finProchains12Mois)}
                                </p>
                                <div className="bg-amber-100 rounded p-2 mb-2">
                                  <p className="font-medium text-amber-800">
                                    ⚠️ {recommandation.moisManquants} mois manquants
                                  </p>
                                  <p className="text-amber-700 text-sm">
                                    Du {recommandation.debutPeriodeManquante.toLocaleDateString('fr-FR')}{' '}
                                    au {recommandation.finPeriodeManquante.toLocaleDateString('fr-FR')}
                                  </p>
                                </div>
                                <p className="text-gray-700">
                                  💡 Économies estimées (remboursement) : <strong className="text-emerald-600">≈ {formatCurrency(recommandation.economiesEstimees)}</strong>
                                </p>
                                <p className="text-xs text-gray-500 mt-1">
                                  Calcul : {formatCurrency(recommandation.economiesMensuellesRemboursement)}/mois × {recommandation.moisManquants} mois
                                </p>
                              </div>
                            )}
                            
                            {/* PÉRIODE FISCALE PRÉCÉDENTE */}
                            {recommandation.hasPeriodePrecedente && (
                              <div className="bg-purple-50 border border-purple-300 rounded-lg p-4 mt-4">
                                <p className="font-semibold text-purple-800 mb-2">
                                  ⏪ Période fiscale précédente (remboursement CERFA)
                                </p>
                                <div className="bg-purple-100 rounded p-2 mb-2">
                                  <p className="font-medium text-purple-800">
                                    📆 12 mois précédant votre analyse
                                  </p>
                                  <p className="text-purple-700 text-sm">
                                    Du {recommandation.debutPeriodePrecedente.toLocaleDateString('fr-FR')}{' '}
                                    au {recommandation.finPeriodePrecedente.toLocaleDateString('fr-FR')}
                                  </p>
                                </div>
                                <p className="text-gray-700">
                                  💡 Économies estimées (remboursement) : <strong className="text-emerald-600">≈ {formatCurrency(recommandation.economiesEstimeesPrecedente)}</strong>
                                </p>
                                <p className="text-xs text-gray-500 mt-1">
                                  Calcul : {formatCurrency(recommandation.economiesMensuellesRemboursement)}/mois × 12 mois
                                </p>
                                <p className="text-xs text-gray-500 mt-1">
                                  * Cette période peut faire l'objet d'une demande de remboursement via le formulaire CERFA.
                                </p>
                              </div>
                            )}
                            
                            {/* TOTAL ESTIMÉ */}
                            {(recommandation.economiesEstimees > 0 || recommandation.economiesEstimeesPrecedente > 0) && (
                              <div className="bg-gradient-to-r from-emerald-100 to-emerald-50 border-2 border-emerald-400 rounded-lg p-4 mt-4">
                                <p className="font-semibold text-emerald-800 mb-2">🎯 Total des économies supplémentaires estimées (remboursement)</p>
                                <p className="text-3xl font-bold text-emerald-600">
                                  ≈ {formatCurrency((recommandation.economiesEstimees || 0) + (recommandation.economiesEstimeesPrecedente || 0))}
                                </p>
                                <p className="text-xs text-gray-500 mt-2">
                                  * Estimation basée sur le remboursement moyen mensuel de la période analysée ({formatCurrency(recommandation.economiesMensuellesRemboursement)}/mois). Les montants réels dépendront des consommations effectives.
                                </p>
                              </div>
                            )}
                            
                            {/* RECOMMANDATION FINALE */}
                            <div className="bg-white border border-blue-200 rounded-lg p-4 mt-4">
                              <p className="font-semibold text-blue-800 mb-2">💡 Notre recommandation</p>
                              <p className="text-gray-700">
                                Pour maximiser vos économies potentielles, nous vous recommandons de réaliser des analyses complémentaires :
                              </p>
                              <ul className="list-disc list-inside mt-2 space-y-1 text-gray-700">
                                {recommandation.hasPeriodeFuture && recommandation.moisManquants > 0 && (
                                  <li>
                                    <strong>Période future :</strong> {recommandation.debutPeriodeManquante.toLocaleDateString('fr-FR')} au {recommandation.finPeriodeManquante.toLocaleDateString('fr-FR')}
                                    <span className="text-emerald-600 font-medium"> (≈ {formatCurrency(recommandation.economiesEstimees)})</span>
                                  </li>
                                )}
                                {recommandation.hasPeriodePrecedente && (
                                  <li>
                                    <strong>Période précédente :</strong> {recommandation.debutPeriodePrecedente.toLocaleDateString('fr-FR')} au {recommandation.finPeriodePrecedente.toLocaleDateString('fr-FR')}
                                    <span className="text-emerald-600 font-medium"> (≈ {formatCurrency(recommandation.economiesEstimeesPrecedente)})</span>
                                  </li>
                                )}
                              </ul>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })()}
              </>
            ) : (
              <div className="bg-amber-50 rounded-xl border-2 border-amber-200 p-8">
                <div className="flex items-center gap-4">
                  <XCircle className="w-12 h-12 text-amber-500" />
                  <div>
                    <h3 className="text-2xl font-bold text-gray-900 mb-2">Non éligible aux taux réduits</h3>
                    <p className="text-gray-600">
                      Votre ratio TICFE/VA de {formatPercent(ratioGlobal)} est inférieur au seuil 
                      de 0,5% requis pour bénéficier des taux réduits.
                    </p>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* TAB 3: Détails PDL */}
        {activeTab === 'details' && (
          <div className="space-y-6">
            {/* Tableau des PDL */}
            <div className="bg-white rounded-xl border border-gray-200 p-6">
              <h3 className="font-semibold text-gray-900 mb-1">Points de Livraison</h3>
              <p className="text-gray-500 text-sm mb-6">Détail de la consommation et TICFE par PDL</p>
              
              <table className="w-full">
                <thead>
                  <tr className="border-b border-gray-200">
                    <th className="text-left py-3 font-medium text-gray-600">PDL</th>
                    <th className="text-left py-3 font-medium text-gray-600">Code NAF</th>
                    <th className="text-right py-3 font-medium text-gray-600">Puissance</th>
                    <th className="text-center py-3 font-medium text-gray-600">Domaine tension</th>
                    <th className="text-right py-3 font-medium text-gray-600">Consommation</th>
                    <th className="text-right py-3 font-medium text-gray-600">TICFE réel</th>
                    <th className="text-right py-3 font-medium text-gray-600">TICFE CERFA</th>
                  </tr>
                </thead>
                <tbody>
                  {rapport.pdls_data?.map((pdl, index) => {
                    const TENSION_LABEL = { bt_inf: 'BT inf.', bt_sup: 'BT sup.', ht: 'HTA', hta: 'HTA' };
                    const tension = pdl.categorie_tension || '';
                    const overridden = !!pdl.categorie_tension_override;
                    return (
                      <tr key={index} className="border-b border-gray-100 hover:bg-gray-50">
                        <td className="py-4 font-mono text-sm text-gray-900">{pdl.pdl}</td>
                        <td className="py-4 text-gray-600">{pdl.code_naf}</td>
                        <td className="py-4 text-right font-mono text-gray-500">{pdl.puissance || pdl.puissance_souscrite} kW</td>
                        <td className="py-4 text-center">
                          <span
                            className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium ${
                              overridden ? 'bg-amber-100 text-amber-700' : 'bg-gray-100 text-gray-600'
                            }`}
                            title={overridden ? 'Domaine forcé manuellement' : 'Domaine déduit automatiquement de la puissance'}
                          >
                            {TENSION_LABEL[tension] || '—'}{overridden ? ' • forcé' : ''}
                          </span>
                        </td>
                        <td className="py-4 text-right font-mono text-blue-600 font-medium">{formatNumber(pdl.total_conso_mwh)} MWh</td>
                        <td className="py-4 text-right font-mono text-gray-700">{formatCurrency(pdl.total_ticfe)}</td>
                        <td className="py-4 text-right font-mono font-medium text-blue-700">{formatCurrency(pdl.ticfe_cerfa_officiel || pdl.total_ticfe)}</td>
                      </tr>
                    );
                  })}
                  <tr className="bg-gray-900 text-white">
                    <td colSpan="4" className="py-4 px-4 font-bold rounded-bl-lg">TOTAL</td>
                    <td className="py-4 text-right font-mono font-bold">{formatNumber(totalConsoMWh)} MWh</td>
                    <td className="py-4 text-right font-mono font-bold">{formatCurrency(totalTICFEReel)}</td>
                    <td className="py-4 text-right font-mono font-bold rounded-br-lg">{formatCurrency(totalTICFECerfaOfficiel)}</td>
                  </tr>
                </tbody>
              </table>
            </div>

            {/* Graphique répartition par PDL */}
            {consoParPDL.length > 1 && (
              <div className="bg-white rounded-xl border border-gray-200 p-6">
                <h3 className="font-semibold text-gray-900 mb-1">Répartition de la consommation</h3>
                <p className="text-gray-500 text-sm mb-6">Consommation par point de livraison</p>
                
                <div className="h-64">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={consoParPDL} layout="vertical" margin={{ left: 60 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                      <XAxis type="number" tickFormatter={(v) => `${v.toFixed(0)} MWh`} />
                      <YAxis type="category" dataKey="name" tick={{ fontSize: 12 }} />
                      <Tooltip 
                        formatter={(value) => `${formatNumber(value)} MWh`}
                        contentStyle={{ backgroundColor: '#fff', border: '1px solid #E5E7EB', borderRadius: '8px' }}
                      />
                      <Bar dataKey="conso" name="Consommation" fill="#3B82F6" radius={[0, 4, 4, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </div>
            )}

            {/* Informations entreprise et période */}
            <div className="grid grid-cols-2 gap-6">
              <div className="bg-white rounded-xl border border-gray-200 p-6">
                <div className="flex items-center gap-2 mb-4">
                  <Building2 className="w-5 h-5 text-blue-500" />
                  <h3 className="font-semibold text-gray-900">Informations Entreprise</h3>
                </div>
                <div className="space-y-3">
                  <div className="flex justify-between py-2 border-b border-gray-100">
                    <span className="text-gray-500">Raison sociale</span>
                    <span className="font-medium text-gray-900">{rapport.nom_entreprise}</span>
                  </div>
                  <div className="flex justify-between py-2 border-b border-gray-100">
                    <span className="text-gray-500">SIREN</span>
                    <span className="font-mono font-medium text-gray-900">{rapport.siren}</span>
                  </div>
                  <div className="flex justify-between py-2 border-b border-gray-100">
                    <span className="text-gray-500">Valeur Ajoutée</span>
                    <span className="font-mono font-medium text-gray-900">{formatCurrency(rapport.valeur_ajoutee)}</span>
                  </div>
                  <div className="flex justify-between py-2">
                    <span className="text-gray-500">Energy Manager</span>
                    <span className="font-medium text-gray-900">{rapport.energy_manager}</span>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-xl border border-gray-200 p-6">
                <div className="flex items-center gap-2 mb-4">
                  <Calendar className="w-5 h-5 text-amber-500" />
                  <h3 className="font-semibold text-gray-900">Période d'analyse</h3>
                </div>
                <div className="space-y-3">
                  <div className="flex justify-between py-2 border-b border-gray-100">
                    <span className="text-gray-500">Début période</span>
                    <span className="font-medium text-gray-900">
                      {rapport.debut_periode ? new Date(rapport.debut_periode).toLocaleDateString('fr-FR') : '-'}
                    </span>
                  </div>
                  <div className="flex justify-between py-2 border-b border-gray-100">
                    <span className="text-gray-500">Fin période</span>
                    <span className="font-medium text-gray-900">
                      {rapport.fin_periode ? new Date(rapport.fin_periode).toLocaleDateString('fr-FR') : '-'}
                    </span>
                  </div>
                  <div className="flex justify-between py-2">
                    <span className="text-gray-500">Date d'analyse</span>
                    <span className="font-medium text-gray-900">
                      {rapport.date_analyse ? new Date(rapport.date_analyse).toLocaleDateString('fr-FR') : '-'}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* TAB 4: Éligibilité */}
        {activeTab === 'eligibility' && (
          <div className="space-y-6">
            {/* Conditions d'éligibilité */}
            <div className="bg-white rounded-xl border border-gray-200 p-6">
              <h3 className="font-semibold text-gray-900 mb-1">Conditions d'éligibilité TICFE</h3>
              <p className="text-gray-500 text-sm mb-6">Vérification des critères pour l'obtention du taux réduit</p>
              
              <div className="space-y-4">
                {/* Critère ratio */}
                <div className={`flex items-center gap-3 p-4 rounded-lg ${
                  ratioGlobal >= 0.005 
                    ? 'bg-emerald-50 border border-emerald-200' 
                    : 'bg-red-50 border border-red-200'
                }`}>
                  {ratioGlobal >= 0.005 ? (
                    <CheckCircle2 className="w-6 h-6 text-emerald-500" />
                  ) : (
                    <XCircle className="w-6 h-6 text-red-500" />
                  )}
                  <div>
                    <p className="font-medium text-gray-900">Ratio TICFE / Valeur Ajoutée ≥ 0,5%</p>
                    <p className="text-sm text-gray-500">Votre ratio : {formatPercent(ratioGlobal)}</p>
                  </div>
                </div>

                {/* Critère NAF */}
                <div className="flex items-center gap-3 p-4 bg-emerald-50 border border-emerald-200 rounded-lg">
                  <CheckCircle2 className="w-6 h-6 text-emerald-500" />
                  <div>
                    <p className="font-medium text-gray-900">Code NAF éligible</p>
                    <p className="text-sm text-gray-500">Activité économique correspondant aux critères d'électro-intensivité</p>
                  </div>
                </div>

                {/* Critère données */}
                <div className="flex items-center gap-3 p-4 bg-emerald-50 border border-emerald-200 rounded-lg">
                  <CheckCircle2 className="w-6 h-6 text-emerald-500" />
                  <div>
                    <p className="font-medium text-gray-900">Données de consommation disponibles</p>
                    <p className="text-sm text-gray-500">
                      {rapport.pdls_data?.length || 0} PDL analysés • {formatNumber(totalConsoMWh, 1)} MWh total
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Conclusion */}
            <div className={`rounded-xl border-2 p-8 ${
              isEligible 
                ? 'border-emerald-200 bg-emerald-50' 
                : 'border-red-200 bg-red-50'
            }`}>
              <div className="flex items-center gap-4">
                {isEligible ? (
                  <CheckCircle2 className="w-12 h-12 text-emerald-500" />
                ) : (
                  <XCircle className="w-12 h-12 text-red-500" />
                )}
                <div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-2">
                    {isEligible ? 'Éligible au taux réduit TICFE' : 'Non éligible au taux réduit'}
                  </h3>
                  <p className="text-gray-600">
                    {isEligible 
                      ? `Vous pouvez bénéficier du taux réduit de ${rapport.taux_optimise_moyen} €/MWh au lieu du taux plein.`
                      : `Votre ratio TICFE/VA de ${formatPercent(ratioGlobal)} ne permet pas l'accès au taux réduit.`
                    }
                  </p>
                </div>
              </div>
            </div>

            {/* Contact */}
            <div className="bg-gray-900 rounded-xl p-8 text-white">
              <h3 className="text-xl font-semibold mb-6">Nous Contacter</h3>
              
              <div className="grid grid-cols-2 gap-6">
                <div>
                  <p className="text-gray-400 text-sm mb-1">Votre conseiller</p>
                  <p className="font-semibold">
                    {rapport.commercial 
                      ? `${rapport.commercial.prenom} ${rapport.commercial.nom}`
                      : rapport.energy_manager || 'Place des Énergies'
                    }
                  </p>
                  {rapport.commercial?.agence && (
                    <p className="text-gray-400 text-sm mt-1">Agence {rapport.commercial.agence}</p>
                  )}
                </div>
                <div>
                  <p className="text-gray-400 text-sm mb-1">Email</p>
                  <a 
                    href={`mailto:${rapport.commercial?.email || 'contact@placedesenergies.com'}`} 
                    className="text-blue-400 hover:text-blue-300"
                  >
                    {rapport.commercial?.email || 'contact@placedesenergies.com'}
                  </a>
                </div>
                {rapport.commercial?.telephone && (
                  <div>
                    <p className="text-gray-400 text-sm mb-1">Téléphone</p>
                    <p className="font-semibold">{rapport.commercial.telephone}</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* TAB 5: Informations (Suite à donner + Responsabilité) */}
        {activeTab === 'informations' && (
          <div className="space-y-6" data-testid="tab-informations">
            <div className="bg-white rounded-xl border border-gray-200 p-8" data-testid="section-suite-a-donner">
              <h2 className="text-lg font-semibold text-[#191D1E] mb-4 flex items-center gap-2">
                <ClipboardList className="w-5 h-5 text-emerald-500" />
                Suite à donner
              </h2>
              <div className="text-sm text-gray-600 leading-relaxed space-y-3">
                <p>En cas d'accord du client pour la mise en place de l'optimisation TICFE, les étapes suivantes seront engagées :</p>
                <ul className="list-disc list-inside space-y-2 ml-2">
                  <li>Collecte et vérification des pièces justificatives (liasse fiscale, attestation sur l'honneur, factures d'électricité de la période fiscale)</li>
                  <li>Préparation et envoi de l'attestation d'éligibilité au fournisseur d'énergie pour application du tarif réduit si concerné</li>
                  <li>Constitution et dépôt du dossier CERFA auprès de l'administration fiscale pour le remboursement de la TICFE sur les périodes passées éligibles</li>
                  <li>Suivi du traitement du dossier et accompagnement jusqu'à l'obtention effective des économies</li>
                </ul>
              </div>
            </div>

            <div className="bg-white rounded-xl border border-gray-200 p-8" data-testid="section-responsabilite">
              <h2 className="text-lg font-semibold text-[#191D1E] mb-4 flex items-center gap-2">
                <ShieldAlert className="w-5 h-5 text-amber-500" />
                Responsabilité
              </h2>
              <p className="text-xs text-gray-500 mb-4 italic">
                Nature de la prestation et limites contractuelles
              </p>

              <p className="text-sm text-gray-600 leading-relaxed mb-3">
                La mission TICFE assurée par <strong>Place des Énergies</strong> constitue une <strong>prestation
                de conseil et d'assistance réglementaire</strong>. Elle ne comporte ni courtage en énergie, ni
                négociation avec le fournisseur, ni déclaration fiscale au nom du Client. Les montants présentés
                dans ce rapport sont des <strong>estimations</strong> établies à partir des données transmises par
                le Client (factures, courbes de charge, code NAF, valeur ajoutée, déclarations fiscales) et de la
                réglementation en vigueur à la date d'analyse. À ce titre, ils ne constituent pas une garantie
                de résultat.
              </p>

              <p className="text-sm text-gray-600 leading-relaxed mb-2">
                La responsabilité de Place des Énergies ne saurait être engagée, notamment, dans les cas suivants&nbsp;:
              </p>
              <ul className="text-sm text-gray-600 leading-relaxed space-y-1.5 mb-3 list-disc pl-6 marker:text-[#397DFF]">
                <li>décision de refus ou de réduction du remboursement par le fournisseur d'énergie ou par l'administration fiscale&nbsp;;</li>
                <li>modification de la réglementation TICFE intervenue postérieurement à la signature du devis et affectant l'éligibilité du Client&nbsp;;</li>
                <li>informations erronées, incomplètes ou tardives transmises par le Client&nbsp;;</li>
                <li>délais de traitement par le fournisseur d'énergie (généralement 3 à 18 mois selon les dossiers) ou par l'administration&nbsp;;</li>
                <li>prescription des créances antérieures à la période non prescrite au moment de la mission&nbsp;;</li>
                <li>écart entre le montant estimé récupérable figurant au présent rapport et le montant effectivement remboursé par le fournisseur ou accepté par l'administration&nbsp;;</li>
                <li>contrôle, redressement ou réclamation ultérieure de la Direction Générale des Finances Publiques (DGFiP) ou de tout autre service de l'État portant sur les déclarations fiscales propres du Client (TVA, valeur ajoutée, TICFE)&nbsp;;</li>
                <li>toute conséquence fiscale, financière ou administrative résultant d'une inexactitude dans les documents ou déclarations fournis par le Client&nbsp;;</li>
                <li>erreur ou omission commise par le Client ou son expert-comptable lors de la déclaration de récupération, Place des Énergies n'intervenant pas dans la procédure déclarative fiscale.</li>
              </ul>

              <p className="text-sm text-gray-600 leading-relaxed mb-3">
                <strong>En tout état de cause</strong>, la responsabilité financière de Place des Énergies, si elle
                est prouvée, est plafonnée au montant des honoraires HT effectivement perçus au titre de la
                présente mission.
              </p>

              <p className="text-xs text-gray-500 leading-relaxed italic border-t border-gray-100 pt-3">
                Les présentes mentions <strong>résument</strong> les dispositions applicables et <strong>ne s'y
                substituent pas</strong>. Pour le détail complet des engagements réciproques des parties, il
                convient de se reporter aux <em>Conditions Générales de Vente TICFE</em> signées entre le Client
                et Place des Énergies.
              </p>
            </div>

            {!readOnly && id && (
              <RapportAuditHistory rapportType="ticfe" rapportId={id} />
            )}
          </div>
        )}

      </main>

      {/* Footer */}
      <footer className="border-t border-gray-200 mt-12">
        <div className="max-w-6xl mx-auto px-6 py-6 flex items-center justify-between text-sm text-gray-400">
          <span>Place des Énergies — Rapport TICFE généré le {new Date().toLocaleDateString('fr-FR')}</span>
          <span>SIREN {rapport.siren}</span>
        </div>
      </footer>
    </div>
  );
};

export default RapportTICFE;
